<template>
    <div>
        <!-- The select renders immediately with an empty options list while
             the products load internally — no flash, per UX request.
             `sgaProductoRequerido` still blocks submit while products exist
             but none is selected. -->
        <div v-show="state === 'ready'">
            <BaseMultiselect
                :label="'Productos asociados al evento'"
                name="products"
                :value="selected"
                :options="products"
                label-key="name"
                value-key="id"
                rules="sgaProductoRequerido"
                @input="onSelect"
            />
        </div>

        <!-- Empty: no products associated to this SGA event. Products are
             mandatory, so the message tells the user AND the `no-products`
             event lets FormEvento block the submit. The loading state renders
             nothing (no flash) while the fetch resolves internally. -->
        <div v-if="state === 'empty'" class="tw:text-xs tw:bg-red-50 tw:border tw:border-red-800 tw:rounded tw:py-1 tw:px-2 tw:text-red-700">
            {{ emptyMessage }}
        </div>
    </div>
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import store from '@/store';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    const props = defineProps({
        sgaEventId: { type: String, required: true },
        sgaProductId: { type: String, default: null }
    });

    const emit = defineEmits(['selected', 'no-products', 'deselected']);

    const emptyMessage = 'Este evento SGA no tiene productos asociados. Los productos son obligatorios para crear el evento.';

    const products = ref([]);
    const selected = ref(null);
    const state = ref('loading');

    onMounted(() => {
        fetchProducts();
    });

    watch(() => props.sgaEventId, () => {
        fetchProducts();
    });

    function fetchProducts() {
        products.value = [];
        selected.value = null;
        state.value = 'loading';

        const alias = store.getters.getConfig?.getVuexAlias?.('back.sga.events.products') || 'BACK_SGA_EVENTS_PRODUCTS';
        const params = {
            filters: {
                sga_event_id: props.sgaEventId
            }
        };

        store.dispatch(alias, params).then(res => {
            products.value = res.data.data;

            if (props.sgaProductId) {
                const preselected = products.value.find(p => p.id === props.sgaProductId);
                if (preselected) {
                    selected.value = preselected.id;
                }
            }
            // Single-product lists are always auto-selected, even when a
            // stale sgaProductId was preloaded (e.g. the saved product no
            // longer exists after switching SGA events). The preselected
            // branch above already covers the case where the preloaded id
            // matches the only product, so this fallback is safe.
            if (selected.value === null && products.value.length === 1) {
                const product = products.value[0];
                selected.value = product.id;
                emit('selected', product);
            }

            // Ready whenever the fetch succeeded, even with a stale preloaded
            // id: the select shows, letting the user pick one of the listed
            // products. Empty list → the empty state (mandatory products).
            state.value = products.value.length ? 'ready' : 'empty';
            if (state.value === 'empty') {
                emit('no-products');
            }
        }).catch(() => {
            // Fetch failure also lands in the empty state: the mandatory
            // message applies and the submit stays blocked.
            state.value = 'empty';
            emit('no-products');
        });
    }

    function onSelect(val) {
        const product = products.value.find(p => p.id === val);
        if (product) {
            emit('selected', product);
        } else if (val === null) {
            // Deselect: clear sga_product_id in the parent (FormEvento).
            emit('deselected');
        }
    }
</script>
